import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "@/hooks/use-toast";
import { ChevronDown } from "lucide-react";
import type { AdminDriver, AdminRace, Matchup } from "../types";

const DriverPairPicker = ({
  race,
  activeDrivers,
  matchups,
  invalidateAll,
}: {
  race: AdminRace;
  activeDrivers: AdminDriver[];
  matchups: Matchup[];
  invalidateAll: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const usedNumbers = useMemo(
    () => new Set(matchups.flatMap((m) => [m.driver_a_number, m.driver_b_number])),
    [matchups],
  );
  // Grouped by team so teammates always sit next to each other — the most
  // common pairing, and the drivers query orders by full_name which scatters them.
  const unusedDrivers = useMemo(
    () =>
      activeDrivers
        .filter((d) => !usedNumbers.has(d.driver_number))
        .sort((a, b) => a.team_name.localeCompare(b.team_name) || a.driver_number - b.driver_number),
    [activeDrivers, usedNumbers],
  );

  useEffect(() => {
    if (unusedDrivers.length === 0) {
      setOpen(false);
      setArmed(null);
    }
  }, [unusedDrivers.length]);

  const handleTap = async (driverNumber: number) => {
    if (armed === null) {
      setArmed(driverNumber);
      return;
    }
    if (armed === driverNumber) {
      setArmed(null);
      return;
    }
    const dA = activeDrivers.find((d) => d.driver_number === armed);
    const dB = activeDrivers.find((d) => d.driver_number === driverNumber);
    setArmed(null);
    if (!dA || !dB) return;
    setSaving(true);
    try {
      const reason = dA.team_name === dB.team_name ? "teammates" : "standings_proximity";
      const { error } = await supabase.rpc("admin_insert_matchup", {
        p_race_id: race.id,
        p_driver_a: dA.driver_number,
        p_driver_b: dB.driver_number,
        p_pairing_reason: reason,
        p_narrative: "",
        p_display_order: matchups.length + 1,
        p_status: "draft",
      });
      if (error) throw error;
      toast({ title: `${dA.name_acronym} vs ${dB.name_acronym} added` });
      invalidateAll();
    } catch (e) {
      toast({ title: "Error", description: (e as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (unusedDrivers.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md border border-dashed px-3 py-2 text-sm font-medium text-muted-foreground">
        <span>Add a matchup by hand ({unusedDrivers.length} left)</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        {/* No "armed" hint: the highlighted tile already says which driver is
            selected, and inserting a line of text shifted the grid down. */}
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
          {unusedDrivers.map((d) => {
            const isArmed = armed === d.driver_number;
            return (
              <button
                key={d.driver_number}
                disabled={saving}
                onClick={() => handleTap(d.driver_number)}
                className={`flex flex-col items-center gap-1 rounded-lg border-2 p-2 text-center transition
                  ${saving ? "opacity-50" : "active:scale-95"}
                  ${isArmed ? "border-primary bg-primary/10" : "border-transparent bg-muted/50"}`}
              >
                {d.headshot_url ? (
                  <img
                    src={d.headshot_url}
                    alt={d.name_acronym}
                    className="h-10 w-10 rounded-full object-cover"
                    style={{ boxShadow: `0 0 0 2px #${d.team_colour ?? "666"}` }}
                  />
                ) : (
                  <span
                    className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold"
                    style={{ backgroundColor: `#${d.team_colour ?? "666"}22`, boxShadow: `0 0 0 2px #${d.team_colour ?? "666"}` }}
                  >
                    {d.name_acronym}
                  </span>
                )}
                {d.headshot_url && <span className="text-[10px] font-semibold leading-tight">{d.name_acronym}</span>}
              </button>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default DriverPairPicker;
